import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { resetPassword } from '@/lib/auth';
import { AlertCircleIcon } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

const formSchema = z
  .object({
    password: z.string(),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Confirm password must match new password',
    path: ['confirmPassword'],
  });

export function ConfirmResetPassword() {
  const [searchParams] = useSearchParams();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });
  const onSubmit = (values: z.infer<typeof formSchema>) => {
    resetPassword(searchParams.get('oobCode')!, values.password).catch(() => {
      form.setError('root', {
        message: 'The email or password you entered is incorrect.',
      });
    });
  };
  return (
    <div className="mx-auto grid w-[350px] gap-6">
      <div className="grid gap-2 text-center">
        <h1 className="text-3xl font-bold">Reset password</h1>
        <p className="text-balance text-muted-foreground">
          Enter your email below to login to your account
        </p>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>New password</FormLabel>
                <FormControl>
                  <Input
                    inError={!!form.formState.errors.root}
                    placeholder="New password"
                    type="password"
                    {...field}
                  />
                </FormControl>
                {form.formState.errors.root && (
                  <FormMessage className="flex text-xs gap-2">
                    <AlertCircleIcon className="h-4 w-4 items-center" />
                    {`${form.formState.errors.root.message}`}
                  </FormMessage>
                )}
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm new password</FormLabel>
                <FormControl>
                  <Input
                    inError={!!form.formState.errors.confirmPassword}
                    placeholder="Confirm new password"
                    type="password"
                    {...field}
                  />
                </FormControl>
                {form.formState.errors.confirmPassword && (
                  <FormMessage className="flex text-xs gap-2">
                    <AlertCircleIcon className="h-4 w-4 items-center" />
                    {`${form.formState.errors.confirmPassword.message}`}
                  </FormMessage>
                )}
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full">
            Confirm
          </Button>
          <Link to="/login" className="w-fit">
            <Button variant="link" type="button">
              Back to login
            </Button>
          </Link>
        </form>
      </Form>
    </div>
  );
}
